import AppButton from '@/components/ui/AppButton';
import AppRadioButton from '@/components/ui/AppRadioButton';
import { endpoints } from '@/services/endpoints';
import { axiosPatchRequest } from '@/services/Request';
import { ITicketData } from '@/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { X, Check, ShieldBan, MessageSquareX } from 'lucide-react';
import { toast } from 'react-toastify';
import avatar from '@/assets/images/image.png';
import MaleAvatar from '@/assets/images/Male-avatar.png';

const DEFAULT_REJECTION_NOTES = [
  'Your ticket was reviewed and does not meet our guidelines.',
  'This report could not be verified with the available information.',
  'The issue described is outside the scope of our moderation policy.',
  'Insufficient evidence to take action at this time.',
];

export default function TicketActionBar({ ticket }: { ticket: ITicketData }) {
  const { ticket_type, reported_user, _id } = ticket;
  const user_name = reported_user?.user_name ?? 'Unknown User';
  const reported_id = reported_user?.reported_id;
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const queryClient = useQueryClient();

  return (
    <>
      <div className="w-full bg-white px-4 py-2">
        <div className="flex items-center justify-between">
          {(ticket_type === 'chat' || ticket_type === 'user') && (
            <div className="flex items-center gap-3">
              <img
                src={ticket_type === 'chat' ? avatar : MaleAvatar}
                alt="avatar"
                className="h-8 w-8 rounded-full ring-2 ring-coal-200"
              />
              <span className="text-sm font-medium text-coal-800">{user_name}</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <AppButton theme="success" size="sm" onClick={() => setShowAcceptModal(true)}>
              <Check className="h-4 w-4" />
              Accept
            </AppButton>
            <AppButton
              theme="error"
              size="sm"
              onClick={() => setShowRejectModal(true)}
            >
              <X className="h-4 w-4" />
              Reject
            </AppButton>
          </div>
        </div>
      </div>

      {showAcceptModal && (
        <AcceptTicket
          ticket={ticket}
          reportedId={reported_id}
          onClose={() => setShowAcceptModal(false)}
        />
      )}

      {showRejectModal && (
        <RejectTicket
          ticketId={_id}
          queryClient={queryClient}
          onClose={() => setShowRejectModal(false)}
        />
      )}
    </>
  );
}

// ─── Reject Modal ─────────────────────────────────────────────────────────────

function RejectTicket({
  ticketId,
  queryClient,
  onClose,
}: {
  ticketId: string;
  queryClient: ReturnType<typeof useQueryClient>;
  onClose: () => void;
}) {
  const [noteMode, setNoteMode] = useState<'default' | 'custom'>('default');
  const [selectedDefault, setSelectedDefault] = useState(DEFAULT_REJECTION_NOTES[0]);
  const [customNote, setCustomNote] = useState('');

  const rejectTicket = useMutation({
    mutationFn: (adminFeedback: string) =>
      axiosPatchRequest(
        endpoints.updateSpecificTicket,
        { ticket_status: 'rejected', admin_feedback: adminFeedback },
        { id: ticketId }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
      toast.success('Ticket rejected');
      onClose();
    },
    onError: () => toast.error('Failed to reject ticket'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const note =
      noteMode === 'custom'
        ? customNote.trim() || selectedDefault
        : selectedDefault;
    rejectTicket.mutate(note);
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl border border-coal-200 bg-white shadow-dropdown animate-in fade-in-0 zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
            <div className="flex items-center gap-2">
              <MessageSquareX className="h-5 w-5 text-danger" />
              <h3 className="text-base font-semibold text-coal-900">Reject Ticket</h3>
            </div>
            <button
              className="rounded-md p-1.5 text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-600"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6">
            {/* Note mode toggle */}
            <div>
              <p className="mb-2 text-sm font-semibold text-coal-800">Rejection Note</p>
              <div className="flex gap-6">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                  <input
                    type="radio"
                    className="accent-primary"
                    checked={noteMode === 'default'}
                    onChange={() => setNoteMode('default')}
                  />
                  Use preset
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                  <input
                    type="radio"
                    className="accent-primary"
                    checked={noteMode === 'custom'}
                    onChange={() => setNoteMode('custom')}
                  />
                  Write custom
                </label>
              </div>
            </div>

            {/* Default note picker */}
            {noteMode === 'default' && (
              <div className="flex flex-col gap-2">
                {DEFAULT_REJECTION_NOTES.map(note => (
                  <label
                    key={note}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      selectedDefault === note
                        ? 'border-danger/40 bg-red-50 text-danger'
                        : 'border-coal-200 text-coal-600 hover:border-coal-300 hover:bg-coal-50'
                    }`}
                  >
                    <input
                      type="radio"
                      className="mt-0.5 accent-red-500 shrink-0"
                      checked={selectedDefault === note}
                      onChange={() => setSelectedDefault(note)}
                    />
                    {note}
                  </label>
                ))}
              </div>
            )}

            {/* Custom note textarea */}
            {noteMode === 'custom' && (
              <div>
                <p className="mb-1.5 text-xs text-coal-400">
                  Leave blank to use the first preset note as fallback.
                </p>
                <textarea
                  className="w-full rounded-lg border border-coal-300 p-3 text-sm text-coal-700 transition-all duration-200 placeholder:text-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                  placeholder={DEFAULT_REJECTION_NOTES[0]}
                  value={customNote}
                  onChange={e => setCustomNote(e.target.value)}
                  rows={4}
                  autoFocus
                />
              </div>
            )}

            {/* Preview */}
            <div className="rounded-lg border border-coal-100 bg-coal-50 px-3 py-2">
              <p className="mb-0.5 text-xs font-medium text-coal-400 uppercase tracking-wide">
                Note preview
              </p>
              <p className="text-sm text-coal-700">
                {noteMode === 'custom'
                  ? customNote.trim() || DEFAULT_REJECTION_NOTES[0]
                  : selectedDefault}
              </p>
            </div>

            <div className="flex gap-3">
              <AppButton
                theme="error"
                size="md"
                type="submit"
                loading={rejectTicket.isPending}
                className="flex-1"
              >
                Confirm Rejection
              </AppButton>
              <AppButton
                size="md"
                type="button"
                theme="default"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </AppButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ─── Accept Modal ─────────────────────────────────────────────────────────────

interface AcceptTicketProps {
  ticket: ITicketData;
  reportedId?: string;
  onClose: () => void;
}

function AcceptTicket({ ticket, reportedId, onClose }: AcceptTicketProps) {
  const { _id } = ticket;
  const [adminFeedback, setAdminFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [shouldBlock, setShouldBlock] = useState(false);
  const [suspendedUntil, setSuspendedUntil] = useState('');
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  const updateTicketMutation = useMutation({
    mutationFn: ({ body, isBlocked }: { body: any; isBlocked: boolean }) =>
      axiosPatchRequest(
        endpoints.updateSpecificTicket,
        body,
        { id: _id, ...(isBlocked ? { isBlocked: 'true' } : {}) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
      toast.success('Ticket accepted and processed');
      onClose();
    },
    onError: () => toast.error('Failed to process ticket'),
  });

  const onSubmit: SubmitHandler<FieldValues> = async () => {
    const feedback =
      feedbackType === 'written'
        ? adminFeedback.trim() || 'Your ticket was accepted, appropriate action was taken.'
        : 'Your ticket was accepted, appropriate action was taken.';

    const body: any = { ticket_status: 'resolved', admin_feedback: feedback };
    if (suspendedUntil) body.suspendedUntil = new Date(suspendedUntil).toISOString();

    updateTicketMutation.mutate({ body, isBlocked: shouldBlock && !!reportedId });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl border border-coal-200 bg-white shadow-dropdown animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-coal-900">Accept &amp; Resolve Ticket</h3>
            <button
              className="rounded-md p-1.5 text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-600"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 p-6">
            {/* Penalty type */}
            {reportedId && (
              <div>
                <p className="mb-2 text-sm font-semibold text-coal-800">Penalty</p>
                <div className="flex gap-6">
                  <label htmlFor="ban" className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                    <AppRadioButton value="ban" id="ban" {...register('action')} onChange={() => setShouldBlock(true)} />
                    Ban
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                    <AppRadioButton value="block" type="radio" {...register('action')} onChange={() => setShouldBlock(true)} />
                    Block
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                    <AppRadioButton value="none" type="radio" {...register('action')} onChange={() => setShouldBlock(false)} />
                    No Penalty
                  </label>
                </div>
              </div>
            )}

            {/* Block warning */}
            {reportedId && shouldBlock && (
              <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <ShieldBan className="h-4 w-4 flex-shrink-0 text-red-500" />
                <p className="text-xs text-red-700 font-medium">The reported user will be blocked on submit.</p>
              </div>
            )}

            {/* Suspend until */}
            {reportedId && (
              <div>
                <p className="mb-2 text-sm font-semibold text-coal-800">
                  Suspend Until <span className="font-normal text-coal-400">(optional)</span>
                </p>
                <input
                  type="date"
                  value={suspendedUntil}
                  onChange={e => setSuspendedUntil(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-lg border border-coal-300 px-3 py-2 text-sm text-coal-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                />
              </div>
            )}

            {/* Feedback type */}
            <div>
              <p className="mb-2 text-sm font-semibold text-coal-800">Feedback Type</p>
              <div className="flex gap-6">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                  <AppRadioButton value="written" type="radio" {...register('feedbackType')} onChange={() => setFeedbackType('written')} />
                  Written
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-600">
                  <AppRadioButton value="default" type="radio" {...register('feedbackType')} onChange={() => setFeedbackType('default')} />
                  Default
                </label>
              </div>
            </div>

            <textarea
              className="w-full rounded-lg border border-coal-300 p-3 text-sm text-coal-700 transition-all duration-200 placeholder:text-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-coal-100 disabled:text-coal-400"
              value={adminFeedback}
              onChange={e => setAdminFeedback(e.target.value)}
              placeholder="Write your feedback..."
              disabled={feedbackType !== 'written'}
              rows={3}
            />

            <div className="flex gap-3">
              <AppButton loading={isSubmitting || updateTicketMutation.isPending} size="md" type="submit" className="flex-1">
                Submit
              </AppButton>
              <AppButton size="md" type="button" onClick={onClose} theme="default" className="flex-1">
                Cancel
              </AppButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
